import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { FolderOutline } from '@lyearn/icons';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn, css } from '@lyearn/style';
import { Radio, Stack, Typography, useSnackbar } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { SearchBar } from '@/components/SearchBar';
import VirtualizedList from '@/components/VirtualizedList';
import useResponse from '@/hooks/useResponse';
import SearchListEmptyState from '@/modules/corporate/components/SearchListEmptyState';
import {
  FILE_SEARCH_PROJECT_VARIABLES,
  useFileSearch,
} from '@/modules/tasks/features/actionItems/hooks/useFileSearch';
import useUpdateActionItem from '@/modules/tasks/features/actionItems/hooks/useUpdateActionItem';
import { getFileIcon } from '@/modules/tasks/utils/getFileIcon';

import useFileActions from '../../../hooks/useFileActions';
import { DialogProps } from '../types';

const dialogSize = css`
  max-width: 52rem;
  height: 72rem;
`;

function MoveTaskToProject({ onClose, task }: DialogProps) {
  const { state } = useFileActions({ onSuccess: onClose });
  const { t } = useTranslation();

  const [isFetching, setIsFetching] = useState(false);
  const [search, setSearch] = useState('');

  const {
    files: projects,
    isFetching: isFetchingProjects,
    fetchMore,
    totalCount,
  } = useFileSearch({ search, filters: FILE_SEARCH_PROJECT_VARIABLES });

  const currentSelectedProject = useMemo(() => {
    return projects?.find((project) => project.id === task?.fileId);
  }, [projects, task?.fileId]);

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    currentSelectedProject?.id ?? null,
  );

  const { updateActionItem } = useUpdateActionItem();

  const { showSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();

  const onSubmit = useCallback(async () => {
    if (!selectedProjectId || !task || !('id' in task)) return;

    const selectedProject = projects?.find((project) => project.id === selectedProjectId);
    setIsFetching(true);

    const response = await updateActionItem({
      data: {
        actionItemId: task.id,
        fileId: selectedProjectId,
        toUnsetFields: {
          stageIds: true,
        },
      },
    });

    handleResponse({
      condition: !response.error,
      onSuccess: () => {
        showSnackbar({
          message: t('Task:TaskWasMovedTo', { name: selectedProject?.name }),
        });
        onClose();
      },
    });

    setIsFetching(false);
  }, [
    handleResponse,
    onClose,
    projects,
    selectedProjectId,
    showSnackbar,
    t,
    task,
    updateActionItem,
  ]);

  return (
    <Modal
      action={{
        disabled:
          isFetching ||
          state.isFetching ||
          isFetchingProjects ||
          !selectedProjectId ||
          selectedProjectId === currentSelectedProject?.id,
        isSubmitting: isFetching || state.isFetching || isFetchingProjects,
        text: t('Common:Actions.Move'),
        onSubmit: onSubmit,
      }}
      classes={{ paper: cn('px-0 w-full rounded-16', dialogSize) }}
      header={{
        title: t('Task:MoveToProjectDialog.Title'),
        description: t('Task:MoveToProjectDialog.Description'),
      }}
      onClose={onClose}>
      <Stack className="mx-24 flex-1" direction="col">
        <SearchBar className="grow-0 shrink-0 mb-24" value={search} onChange={setSearch} />
        {!totalCount ? (
          <SearchListEmptyState
            Icon={FolderOutline}
            className="!my-[12rem]"
            search={search}
            title={t('Task:MoveToProjectDialog.EmptyState')}
          />
        ) : (
          <>
            <Typography className="mb-12 mx-8" variant="label-01">
              All Projects
            </Typography>
            <VirtualizedList
              className="flex-1 border border-border rounded-12 overflow-clip"
              data={projects}
              endReached={fetchMore}
              itemContent={(index, project) => (
                <Stack
                  key={project.id}
                  alignItems="center"
                  className={cn('border-divider ps-16 pe-20 py-10 hover:bg-bg-secondary', {
                    'border-t': index > 0,
                  })}
                  direction="row"
                  gap={8}
                  justifyContent="between"
                  onClick={() => setSelectedProjectId(project.id)}>
                  {getFileIcon(project, 22, 'text-icon-primary shrink-0')}
                  <TypographyWithTooltip
                    className="flex-1 truncate"
                    color="text-text-paragraph"
                    numberOfLines={1}
                    variant="body-short-01">
                    {project.name}
                  </TypographyWithTooltip>
                  <Radio
                    checked={selectedProjectId === project.id}
                    onChange={() => setSelectedProjectId(project.id)}
                  />
                </Stack>
              )}
              totalCount={totalCount}
            />
          </>
        )}
      </Stack>
    </Modal>
  );
}

export default MoveTaskToProject;
